import { ReactNode } from 'react';

import DataTableSortHeader from '@components/DataTableSortHeader';
import InfiniteScroll, { LoadMoreArgs, PageInfo } from '@components/InfiniteScroll';
import dataTableStyles from '@theme/DataTable.module.css';
import { SortDirectionEnum } from '@typing/Generated';
import { IconName } from '@utils/iconUtils';

export type TableHeader<SortBy> = {
  columnSortBy?: SortBy;
  extraInfo?: ReactNode;
  hideMobile?: boolean;
  icon?: IconName;
  messageKey?: string;
  messageValues?: Record<string, ReactNode>;
  skip?: boolean;
  textEnd?: boolean;
};

type Props<SortBy> = {
  children: ReactNode;
  /* eslint-disable-next-line @typescript-eslint/no-explicit-any */
  fetchMore?: (args: LoadMoreArgs) => Promise<any>;
  gradientTable?: boolean;
  headerKey?: string;
  headers: TableHeader<SortBy>[];
  pageInfo?: PageInfo | undefined;
  setSortBy?: (sortBy: SortBy) => void;
  setSortDirection?: (sortDirection: SortDirectionEnum) => void;
  skipScroll?: boolean;
  skipSpacing?: boolean;
  sortBy?: SortBy;
  sortDirection?: SortDirectionEnum;
  spacedOutTable?: boolean;
};

const Table = <SortBy extends unknown>({
  children,
  fetchMore,
  gradientTable = false,
  headerKey,
  headers,
  pageInfo,
  setSortBy,
  setSortDirection,
  skipScroll = false,
  skipSpacing = false,
  sortBy,
  sortDirection,
  spacedOutTable
}: Props<SortBy>) => (
  <InfiniteScroll loadMore={fetchMore} pageInfo={pageInfo} skipScroll={skipScroll}>
    <div
      className={`${dataTableStyles.scrollX} ${skipScroll ? dataTableStyles.noScroll : ''} ${
        skipSpacing ? dataTableStyles.skipSpacing : dataTableStyles.messagingSpacing
      } ${spacedOutTable || skipSpacing ? '' : 'ion-margin'}`}
    >
      <table
        className={`${dataTableStyles.dataTable} ${
          spacedOutTable ? dataTableStyles.spacedOutDataTable : gradientTable ? dataTableStyles.gradientTable : ''
        }`}
      >
        <thead>
          <tr
            className={
              spacedOutTable ? dataTableStyles.spacedOutTableRow : gradientTable ? dataTableStyles.gradientTableRow : ''
            }
          >
            {headers.map(header => (
              <DataTableSortHeader<SortBy>
                key={`${header.messageKey ?? ''} ${
                  (header.columnSortBy as unknown as string | null) ?? ''
                } ${Math.random().toString()}`}
                messageKey={
                  (headerKey && header.messageKey) ??
                  `${headerKey ?? ''}.${(header.columnSortBy as unknown as string | null) ?? ''}`
                }
                setSortBy={setSortBy}
                setSortDirection={setSortDirection}
                sortBy={sortBy}
                sortDirection={sortDirection}
                textEnd={false}
                {...header}
              />
            ))}
          </tr>
        </thead>
        <tbody>{children}</tbody>
      </table>
    </div>
  </InfiniteScroll>
);

export default Table;
